import React, { useState, useEffect, useRef, useMemo } from "react";
import "./TransmitterFaceplate.css";
import TrendGraph from "../components/TrendGraph";
import TransmitterHistoryFaceplate from "./TransmitterHistoryFaceplate";
import { useTrend } from "../context/TrendContext";

function TransmitterFaceplate({
  tag = "PT_0000",
  value = 0,
  unit = "",
  min = 0,
  max = 100,
  HH = 90,
  H = 75,
  L = 25,
  LL = 10,
  description = "No description available",
  x = 200,
  y = 120,
  onClose = () => {},
}) {
  // ================= POSITION & DRAG =================
  const [position, setPosition] = useState({ x, y });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ offsetX: 0, offsetY: 0 });

  const startDrag = (e) => {
    setDragging(true);
    dragRef.current.offsetX = e.clientX - position.x;
    dragRef.current.offsetY = e.clientY - position.y;
  };

  useEffect(() => {
    const move = (e) => {
      if (!dragging) return;

      setPosition({
        x: e.clientX - dragRef.current.offsetX,
        y: e.clientY - dragRef.current.offsetY,
      });
    };

    const up = () => setDragging(false);

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [dragging]);

  // ================= PIN STATE =================
  const [pinned, setPinned] = useState(() => {
    return localStorage.getItem(`pin_${tag}`) === "true";
  });

  // ================= LOAD SAVED POSITION =================
  useEffect(() => {
    const savedPos = localStorage.getItem(`pos_${tag}`);

    if (savedPos) {
      try {
        setPosition(JSON.parse(savedPos));
      } catch {}
    }
  }, [tag]);

  // ================= SAVE PIN + META =================
  useEffect(() => {
    localStorage.setItem(`pin_${tag}`, pinned);

    if (pinned) {
      localStorage.setItem(`unit_${tag}`, unit);
      localStorage.setItem(`min_${tag}`, min);
      localStorage.setItem(`max_${tag}`, max);
      localStorage.setItem(`HH_${tag}`, HH);
      localStorage.setItem(`H_${tag}`, H);
      localStorage.setItem(`L_${tag}`, L);
      localStorage.setItem(`LL_${tag}`, LL);
      localStorage.setItem(`description_${tag}`, description);
    }
  }, [pinned, tag, unit, min, max, HH, H, L, LL, description]);

  // ================= SAVE POSITION =================
  useEffect(() => {
    if (pinned) {
      localStorage.setItem(`pos_${tag}`, JSON.stringify(position));
    }
  }, [position, pinned, tag]);

  // ================= 🔥 REAL-TIME SMOOTH VALUE =================
  const [displayValue, setDisplayValue] = useState(value);

  useEffect(() => {
    const interval = setInterval(() => {
      setDisplayValue((prev) => {
        const target = value ?? 0;

        // Faster response (DCS-like)
        const next = prev + (target - prev) * 0.35;

        return isNaN(next) ? 0 : next;
      });
    }, 100); // fast refresh

    return () => clearInterval(interval);
  }, [value]);

  // ================= SAFE VALUE =================
  const safeValue = Math.max(min, Math.min(displayValue ?? 0, max));

  // ================= ALARM LOGIC =================
  const getAlarmState = () => {
    if (safeValue >= HH) return "HH";
    if (safeValue >= H) return "H";
    if (safeValue <= LL) return "LL";
    if (safeValue <= L) return "L";
    return "NORMAL";
  };

  const alarmState = getAlarmState();

  const getAlarmColor = () => {
    switch (alarmState) {
      case "HH":
        return "#ff0000";
      case "H":
        return "#ff9900";
      case "L":
        return "#00bfff";
      case "LL":
        return "#0044ff";
      default:
        return "#00ff66";
    }
  };

  const alarmColor = getAlarmColor();

  const isCritical =
    alarmState === "HH" || alarmState === "LL";

  // ================= GAUGE =================
  const percentage = useMemo(() => {
    return ((safeValue - min) / (max - min || 1)) * 100;
  }, [safeValue, min, max]);

  // ================= FORMAT =================
  const formatValue = (val) => {
    if (val >= 100) return val.toFixed(0);
    if (val >= 10) return val.toFixed(1);
    return val.toFixed(2);
  };

  // ================= CLOSE =================
  const handleClose = () => {
    if (pinned) {
      setPinned(false);
    } else {
      onClose();
    }
  };

  // ================= TREND =================
  const [showTrend, setShowTrend] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const { trendData } = useTrend();

  // ================= RENDER =================
  return (
    <div
      className={`tx-faceplate ${dragging ? "tx-dragging" : ""} ${
        isCritical ? "tx-blink" : ""
      }`}
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      {/* HEADER */}
      <div className="tx-header" onMouseDown={startDrag}>
        <div className="tx-header-left">
          <div className="tx-tag">{tag}</div>
        </div>

        <div className="tx-header-right">
          <button
            className={`tx-btn tx-pin ${pinned ? "active" : ""}`}
            onClick={() => setPinned((prev) => !prev)}
            title="Pin"
          >
            📌
          </button>

          <button
            className="tx-btn tx-close"
            onClick={handleClose}
            title="Close"
          >
            ✕
          </button>
        </div>
      </div>

      {/* VALUE */}
      <div className="tx-value-box">
        <div
          className="tx-value"
          style={{ color: alarmColor }}
        >
          {formatValue(safeValue)}
          <span className="tx-unit">{unit}</span>
        </div>
      </div>

      {/* GAUGE */}
      <div className="tx-gauge-container">
        <div className="tx-scale">
          <div className="tx-scale-max">{max}</div>
          <div className="tx-scale-min">{min}</div>
        </div>

        <div className="tx-gauge">
          <div
            className="tx-gauge-fill"
            style={{
              height: `${percentage}%`,
              background: alarmColor,
            }}
          />

          <div
            className="tx-pointer"
            style={{
              bottom: `${percentage}%`,
            }}
          />
        </div>
      </div>

      {/* ALARM */}
      <div className={`tx-alarm tx-${alarmState}`}>
        {alarmState}
      </div>

      {/* VISUALS */}
      <div className="tx-visuals">
        <div
          className="tx-graph"
          onClick={() => setShowTrend(true)}
        >
          📈 CLICK FOR TREND
        </div>

        <div className="tx-images">
          <div className="tx-img">⚙️</div>
          <div className="tx-img">🌡️</div>
          <div className="tx-img">💧</div>

          <div
            className="tx-img"
            onClick={() => {
              setShowTrend(false);
              setShowHistory(true);
            }}
          >
            📋
          </div>
        </div>
      </div>

      {/* DESCRIPTION */}
      <div className="tx-description">
        {description}
      </div>

      {/* TREND GRAPH */}
      {showTrend && (
        <TrendGraph
          tag={tag}
          onClose={() => setShowTrend(false)}
        />
      )}

      {/* HISTORY */}
      {showHistory && (
        <TransmitterHistoryFaceplate
          tag={tag}
          trendData={trendData}
          onClose={() => setShowHistory(false)}
          onOpenTrend={() => {
            setShowHistory(false);
            setShowTrend(true);
          }}
        />
      )}
    </div>
  );
}

export default TransmitterFaceplate;